import { all, call, put, select, spawn, take } from 'redux-saga/effects';
import i18n from '../../containers/i18n/i18n';
import { getLocale } from '../../containers/i18n/selectors';
import * as notify from '../../containers/layout/Notifications/actions';
import { VIEW_ENTER as OPEN_BETS_VIEW_ENTER } from '../../containers/openBets/actions';
import { VIEW_ENTER as SETTLED_BETS_VIEW_ENTER } from '../../containers/settledBets/actions';
import hidePeriod from '../../utils/hidePeriod';
import { serviceUnavailable, VISIBILITY_CHANGE } from '../app/actions';
import { visibilityChangeChannel } from '../app/channels';
import {
  ConnectionError,
  NotFoundError,
  ServiceUnavailableError,
  UnauthorizedAccessError
} from '../app/errors';
import { restartUserJourney } from '../auth/saga';
import { isAuthenticated } from '../user/selectors';
import {
  ACCOUNT_BALANCE_REQUEST,
  ACCOUNT_DETAILS_REQUEST,
  accountBalanceFailure,
  accountBalanceRequest,
  accountBalanceSuccess,
  accountDetailsFailure,
  accountDetailsRequest,
  accountDetailsSuccess
} from './actions';
import { getAccountBalance, getAccountDetails } from './rest';
import { isAuthenticatedAsOmni, isBalancePending, isDetailsPending } from './selectors';

/* eslint-disable no-constant-condition */
export function* watchAccountDetailsRequest() {
  while (yield take(ACCOUNT_DETAILS_REQUEST)) {
    try {
      const results = yield call(getAccountDetails);
      yield put(accountDetailsSuccess(results));
    } catch (e) {
      yield put(accountDetailsFailure(e));

      if (e instanceof UnauthorizedAccessError) {
        yield call(restartUserJourney);
      } else if (e instanceof ConnectionError) {
        yield put(notify.add('Connection error. Try refresh!', notify.ERROR));
      } else if (e instanceof ServiceUnavailableError) {
        yield put(serviceUnavailable());
      } else {
        throw e;
      }
    }
  }
}

export function* checkAccountBalance() {
  try {
    const results = yield call(getAccountBalance);
    yield put(accountBalanceSuccess(results));
  } catch (e) {
    const locale = yield select(getLocale);
    yield put(accountBalanceFailure(e));
    const isBalanceUnavailable = e instanceof NotFoundError || e instanceof ConnectionError;
    const isUnauthorized = e instanceof UnauthorizedAccessError;
    const isServiceUnavailable = e instanceof ServiceUnavailableError;
    if (isBalanceUnavailable) {
      yield put(notify.add(i18n('onlineBalance', 'serviceUnavailable', locale), notify.ERROR));
    } else if (isUnauthorized) {
      yield call(restartUserJourney);
    } else if (isServiceUnavailable) {
      yield put(serviceUnavailable());
    } else {
      throw e;
    }
  }
}

export function* watchAccountBalanceRequest() {
  const events = [
    ACCOUNT_BALANCE_REQUEST,
    OPEN_BETS_VIEW_ENTER,
    SETTLED_BETS_VIEW_ENTER
  ];

  while (true) {
    const { type, ignoreHidePeriod } = yield take(events);
    const forceRequest = type === ACCOUNT_BALANCE_REQUEST && ignoreHidePeriod;
    const isOmniLinked = yield select(isAuthenticatedAsOmni);
    if (isOmniLinked && (!hidePeriod() || forceRequest)) {
      yield call(checkAccountBalance);
    }
  }
}

export function* watchWindowVisibility() {
  const channel = yield call(visibilityChangeChannel);
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { type, hidden } = yield take(channel);
    const authenticated = yield select(isAuthenticated);
    const authenticatedAsOmni = yield select(isAuthenticatedAsOmni);
    const detailsPending = yield select(isDetailsPending);
    const balancePending = yield select(isBalancePending);
    if (authenticated && !detailsPending && type === VISIBILITY_CHANGE && !hidden) {
      yield put(accountDetailsRequest());
    }
    if (authenticatedAsOmni && !balancePending && type === VISIBILITY_CHANGE && !hidden) {
      yield put(accountBalanceRequest());
    }
  }
}

export default function* root() {
  yield all([
    spawn(watchAccountDetailsRequest),
    spawn(watchAccountBalanceRequest),
    spawn(watchWindowVisibility)
  ]);
}
/* eslint-enable no-constant-condition */
